import { useCallback, useEffect, useRef, useState } from "react";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { FilterChips } from "@/components/ColumnHead";
import { nextSort, type SortState } from "@/lib/columnFilters";
import { Icon } from "@/components/Icon";
import { Later } from "@/components/Later";
import { Failure } from "@/features/shell/Problem";
import { CardsSkeleton, ShipmentCards } from "@/features/shipments/ShipmentCards";
import { ShipmentPanel } from "@/features/shipments/ShipmentPanel";
import { ShipmentsTable, TableSkeleton, useShipmentColumns } from "@/features/shipments/ShipmentsTable";
import { isoDay } from "@/lib/format";
import {
  CMR_ZIP_MAX,
  COLUMNS,
  VIEWS,
  cmrZipHref,
  countsQuery,
  csvHref,
  shipmentsQuery,
  type Column,
  type ListFilter,
  type ShipmentRow,
  type View,
} from "@/lib/shipments";
import { PANEL_WIDE, PHONE, useMedia } from "@/lib/useMedia";

// Zendingen (H-z01 … H-z08, H-z11): the filters with their counts, search, the period for Afgeleverd and Alles,
// the table that scrolls in itself, and one consignment in the panel beside it (from 1100 px; narrower a click
// opens its own page, and a phone gets cards). Everything the screen shows is in the address, so a link or the
// back button gives the same list; only the picked rows are not, and another filter lets go of them. A click on a
// column's name sorts on it, its funnel filters it (26-10-2026): both in the address too (sort, order, filter.<column>).

interface ShipmentsSearch {
  view?: Exclude<View, "active">;
  q?: string;
  from?: string;
  to?: string;
  open?: string;
  sort?: Column;
  order?: "desc";
  [filter: `filter.${string}`]: string | undefined;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A value from the address as text: the router reads "12345" as a number. */
function text(value: unknown): string | undefined {
  if (typeof value === "number") return String(value);
  return typeof value === "string" && value.trim() ? value : undefined;
}

export const Route = createFileRoute("/_portal/shipments")({
  validateSearch: (search: Record<string, unknown>): ShipmentsSearch => {
    const view = VIEWS.find((v) => v !== "active" && v === search.view) as ShipmentsSearch["view"];
    const day = (value: unknown) => (typeof value === "string" && DAY.test(value) ? value : undefined);
    const sort = COLUMNS.find((column) => column === search.sort);
    const filters = Object.fromEntries(COLUMNS.map((column) => [`filter.${column}`, text(search[`filter.${column}`])]).filter(([, value]) => value));
    return {
      view,
      q: text(search.q),
      from: day(search.from),
      to: day(search.to),
      open: text(search.open),
      sort,
      order: sort && search.order === "desc" ? "desc" : undefined,
      ...filters,
    };
  },
  component: ShipmentsScreen,
});

/** The period starts a month back unless the customer chose otherwise. */
const DEFAULT_FROM_DAYS = -30;

function ShipmentsScreen() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const phone = useMedia(PHONE);
  const wide = useMedia(PANEL_WIDE);

  const view: View = search.view ?? "active";
  const withPeriod = view === "delivered" || view === "all";
  const from = search.from ?? isoDay(DEFAULT_FROM_DAYS);
  const filters: Partial<Record<Column, string>> = Object.fromEntries(
    COLUMNS.map((column) => [column, search[`filter.${column}`]]).filter(([, value]) => value),
  );
  const sort: SortState = { sort: search.sort, order: search.sort ? (search.order === "desc" ? "desc" : "asc") : undefined };
  const filter: ListFilter = { view, q: search.q, from, to: search.to, sort: search.sort, order: sort.order, filters };
  const columns = useShipmentColumns();

  const counts = useQuery({ ...countsQuery({ from, to: search.to }), placeholderData: keepPreviousData });
  const list = useInfiniteQuery({ ...shipmentsQuery(filter), placeholderData: keepPreviousData });
  const rows = list.data?.pages.flatMap((page) => page.data) ?? [];

  // The consignments picked for their CMRs (H-z09); a new filter or search starts again.
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set());
  const filterKey = [view, search.q, from, search.to, JSON.stringify(filters)].join("|");
  useEffect(() => setPicked(new Set()), [filterKey]);
  const pick = useCallback(
    (number: string) =>
      setPicked((current) => {
        const next = new Set(current);
        if (!next.delete(number)) next.add(number);
        return next;
      }),
    [],
  );
  const pickAll = useCallback((numbers: string[] | null) => setPicked(new Set(numbers ?? [])), []);

  // Search as you type, in the address a moment after the last key.
  const [typed, setTyped] = useState(search.q ?? "");
  useEffect(() => {
    setTyped((current) => (current.trim() === (search.q ?? "") ? current : (search.q ?? "")));
  }, [search.q]);
  useEffect(() => {
    const q = typed.trim() || undefined;
    if (q === search.q) return;
    const id = window.setTimeout(() => void navigate({ search: (prev) => ({ ...prev, q }), replace: true }), 300);
    return () => window.clearTimeout(id);
  }, [typed, search.q, navigate]);

  const panelOpen = wide && !phone && !!search.open;
  // The width at the click itself: a window made narrower since the last render opens the page, not the panel.
  const open = useCallback(
    (number: string) => {
      if (window.matchMedia(PANEL_WIDE).matches) void navigate({ search: (prev) => ({ ...prev, open: number }) });
      else void navigate({ to: "/shipments/$number", params: { number } });
    },
    [navigate],
  );
  const close = useCallback(() => void navigate({ search: (prev) => ({ ...prev, open: undefined }) }), [navigate]);

  // The next page when the end of the list comes into view.
  const sentinel = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = list;
  useEffect(() => {
    const target = sentinel.current;
    if (!target || !hasNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, rows.length]);

  const setSort = (column: string) => {
    const next = nextSort(sort, column);
    void navigate({ search: (prev) => ({ ...prev, sort: next.sort as Column | undefined, order: next.order === "desc" ? "desc" : undefined }), replace: true });
  };
  const setFilter = (column: string, value: string) =>
    void navigate({ search: (prev) => ({ ...prev, [`filter.${column}`]: value.trim() || undefined }), replace: true });
  const clearFilters = () =>
    void navigate({ search: (prev) => ({ ...prev, ...Object.fromEntries(COLUMNS.map((column) => [`filter.${column}`, undefined])) }), replace: true });
  const filtered = Object.keys(filters).length > 0;

  const setView = (next: View) =>
    void navigate({ search: (prev) => ({ ...prev, view: next === "active" ? undefined : next, open: undefined }) });

  const more = hasNextPage && (
    <div ref={sentinel} className="wl-hstack" style={{ justifyContent: "center", padding: "var(--space-3)" }}>
      <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" disabled={isFetchingNextPage} onClick={() => void fetchNextPage()}>
        {isFetchingNextPage ? t("shipments.loadingMore") : t("shipments.more")}
      </button>
    </div>
  );

  let content;
  if (list.isError && !list.data) {
    content = <Failure error={list.error} onRetry={() => void list.refetch()} />;
  } else if (!list.data) {
    content = <Later>{phone ? <CardsSkeleton /> : <div className="wl-table-wrap"><TableSkeleton narrow={panelOpen} /></div>}</Later>;
  } else if (rows.length === 0) {
    content = <Empty view={view} q={search.q} filtered={filtered} total={counts.data?.all} onAll={() => setView("all")} onClear={clearFilters} />;
  } else if (phone) {
    content = (
      <>
        <ShipmentCards rows={rows} />
        {more}
      </>
    );
  } else {
    content = (
      <div className="wl-table-wrap" aria-busy={list.isFetching && !isFetchingNextPage}>
        <ShipmentsTable
          rows={rows}
          selected={panelOpen ? search.open : undefined}
          narrow={panelOpen}
          onOpen={open}
          picked={picked}
          onPick={pick}
          onPickAll={pickAll}
          sort={sort}
          onSort={setSort}
          filters={filters}
          onFilter={setFilter}
        />
        {more}
      </div>
    );
  }

  return (
    <div className="wl-portal-split">
      <div className="wl-portal-page" data-screen-label="Portaal Zendingen">
        <div className="wl-portal-head">
          <h1 className="t-h2">{t("nav.shipments")}</h1>
          <div className="wl-hstack">
            {!phone && (
              <a className="wl-btn wl-btn--secondary" href={csvHref(filter)}>
                <Icon name="download" />
                {t("shipments.excel")}
              </a>
            )}
            <Link to="/order" className="wl-btn wl-btn--accent">
              <Icon name="plus" />
              {t("nav.order")}
            </Link>
          </div>
        </div>

        <div className="wl-portal-toolbar">
          <div className="wl-segment" role="group" aria-label={t("shipments.views")}>
            {VIEWS.map((v) => (
              <button
                key={v}
                type="button"
                className={v === view ? "wl-segment__btn is-active" : "wl-segment__btn"}
                aria-pressed={v === view}
                onClick={() => setView(v)}
              >
                {t(`shipments.view.${v}`)}
                {counts.data && <span className="count">{counts.data[v]}</span>}
              </button>
            ))}
          </div>
          <label className="wl-input-group">
            <span className="wl-input-group__icon">
              <Icon name="search" />
            </span>
            <input
              type="search"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={t("shipments.search")}
              aria-label={t("shipments.search")}
            />
          </label>
          {withPeriod && (
            <>
              <label className="wl-hstack t-body-sm">
                {t("shipments.periodFrom")}
                <input
                  type="date"
                  className="wl-input"
                  value={from}
                  max={search.to}
                  onChange={(event) => void navigate({ search: (prev) => ({ ...prev, from: event.target.value || undefined }), replace: true })}
                />
              </label>
              <label className="wl-hstack t-body-sm">
                {t("shipments.periodTo")}
                <input
                  type="date"
                  className="wl-input"
                  value={search.to ?? ""}
                  min={from}
                  onChange={(event) => void navigate({ search: (prev) => ({ ...prev, to: event.target.value || undefined }), replace: true })}
                />
              </label>
            </>
          )}
        </div>

        <FilterChips columns={columns} values={filters} onFilter={setFilter} onClear={clearFilters} />

        {!phone && picked.size > 0 && <SelectionBar rows={rows.filter((row) => picked.has(row.number))} onClear={() => pickAll(null)} />}

        {content}
      </div>
      {panelOpen && search.open && <ShipmentPanel key={search.open} number={search.open} onClose={close} />}
    </div>
  );
}

/**
 * What is picked (H-z09): how many, how many of them have a signed CMR, and those as one ZIP. At most 100 at once
 * (W5); everything in a period (H-z10) comes later.
 */
function SelectionBar({ rows, onClear }: { rows: ShipmentRow[]; onClear: () => void }) {
  const { t } = useTranslation();
  const withCmr = rows.filter((row) => row.cmr === "available").map((row) => row.number);
  const tooMany = withCmr.length > CMR_ZIP_MAX;
  return (
    <div className="wl-selection-bar" role="region" aria-label={t("shipments.selected", { count: rows.length })}>
      <span className="wl-selection-bar__count">{t("shipments.selected", { count: rows.length })}</span>
      <span className="wl-selection-bar__meta">· {t("shipments.selectedWithCmr", { count: withCmr.length })}</span>
      {tooMany && <span className="wl-selection-bar__meta">{t("shipments.cmrZipMax", { max: CMR_ZIP_MAX })}</span>}
      <div className="wl-selection-bar__actions">
        {withCmr.length > 0 && !tooMany ? (
          <a className="wl-btn wl-btn--secondary wl-btn--sm" href={cmrZipHref(withCmr)} download>
            <Icon name="download" />
            {t("shipments.downloadCmrs")}
          </a>
        ) : (
          <button type="button" className="wl-btn wl-btn--secondary wl-btn--sm" disabled>
            <Icon name="download" />
            {t("shipments.downloadCmrs")}
          </button>
        )}
        <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={onClear}>
          {t("shipments.clearSelection")}
        </button>
      </div>
    </div>
  );
}

/** Nothing to show: a new customer (H-z07), nothing found (H-z08, with Alles on offer), or an empty filter. */
function Empty({
  view,
  q,
  filtered,
  total,
  onAll,
  onClear,
}: {
  view: View;
  q?: string;
  filtered: boolean;
  total?: number;
  onAll: () => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  if (filtered && !q) {
    return (
      <div className="wl-empty" data-screen-label="Portaal Zendingen niets met deze filters">
        <div className="wl-empty__icon">
          <Icon name="filter" size={20} />
        </div>
        <div className="wl-empty__title">{t("shipments.notFiltered", { view: t(`shipments.view.${view}`) })}</div>
        <p className="wl-empty__text">{t("shipments.notFilteredText")}</p>
        <div className="wl-hstack" style={{ justifyContent: "center" }}>
          <button type="button" className="wl-btn wl-btn--secondary" onClick={onClear}>
            {t("columns.filterClearAll")}
          </button>
          {view !== "all" && (
            <button type="button" className="wl-btn wl-btn--ghost" onClick={onAll}>
              {t("shipments.view.all")}
            </button>
          )}
        </div>
      </div>
    );
  }
  if (q) {
    return (
      <div className="wl-empty" data-screen-label="Portaal Zendingen niets gevonden">
        <div className="wl-empty__icon">
          <Icon name="search" size={20} />
        </div>
        <div className="wl-empty__title">{t("shipments.notFound", { q, view: t(`shipments.view.${view}`) })}</div>
        <p className="wl-empty__text">{view === "all" ? t("shipments.notFoundAll") : t("shipments.notFoundText")}</p>
        {view !== "all" && (
          <button type="button" className="wl-btn wl-btn--secondary" onClick={onAll}>
            {t("shipments.searchAll")}
          </button>
        )}
      </div>
    );
  }
  if (total === 0) {
    return (
      <div className="wl-empty" data-screen-label="Portaal Zendingen leeg">
        <div className="wl-empty__icon">
          <Icon name="truck" size={20} />
        </div>
        <div className="wl-empty__title">{t("shipments.emptyTitle")}</div>
        <p className="wl-empty__text">{t("shipments.emptyText")}</p>
        <Link to="/order" className="wl-btn wl-btn--secondary">
          {t("nav.order")}
        </Link>
      </div>
    );
  }
  return (
    <div className="wl-empty">
      <div className="wl-empty__icon">
        <Icon name="truck" size={20} />
      </div>
      <div className="wl-empty__title">{t(`shipments.none.${view}`)}</div>
    </div>
  );
}
